import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import Tooltip from 'react-tooltip';
import { addTranslation, IntlProps } from 'decorators/addTranslation';
import ManualInputCalendar from 'components/ui/manualInputCalendar';
import DateHelpers from 'helpers/Date';
import { isTopPositionByBlock } from 'helpers/setCorrectPosition';
import DatePickerRange from 'components/ui/datePickerRange/DatePickerRange';
import RelativeDatePicker, { parseRelative, MAX_DAYS_DEFAULT, MAX_DAYS_HOURLY } from 'components/ui/datePickerRange/RelativeDatePicker';
import dateFormats, { groupPeriodBy } from 'constants/dateFormats';
import Panel from 'components/ui/panel';
import { APP_PADDING } from 'constants/ui';
import breakpoints from 'constants/breakpoints';
import InfoIcon from '../infoIcon';
import { RelativePeriod } from 'types/Analytics';

interface OwnProps {
  value: { dateFrom: string; dateTo?: string };
  onChange: (date) => void;
  label?: string;
  placeholder?: string;
  title?: string; //?
  id?: string;
  customClass?: string;
  error?: string;
  minDate?: string;
  maxDate?: string;
  withTime?: boolean;
  isRequired?: boolean;
  isDisabled?: boolean;
  initialDate?: string;
  withCloseButton?: boolean;
  modern?: boolean;
  tooltip?: string;
  isUnselectDate?: boolean;
  onDatePickerToggle?: (isOpen: boolean) => void;
  getRef?: (ref: any) => void;
  containerSelector?: string;
  monthsAmount?: number;
  applyFormat?: keyof typeof dateFormats;
  disableButton?: boolean;
  panelTitle?: string;
  panelPosition?: {
    top: number;
    left?: number;
  };
  enableRelativeMode?: boolean;
  groupByDate?: string;
}

interface ConnectedProps {
  isMenuExpanded: boolean;
}

type ActiveTab = 'absolute' | 'relative';

interface State {
  isOpened: boolean;
  isTopPosition: boolean;
  height: string;
  left: string;
  top: string;
  maxWidth: string;
  activeTab: ActiveTab;
}

type Props = OwnProps & IntlProps & ConnectedProps;

const getMaxDays = (groupByDate?: string): number =>
  groupByDate === groupPeriodBy.Hour ? MAX_DAYS_HOURLY : MAX_DAYS_DEFAULT;

const formatRelativeLabel = (relative: string, getTranslateFn: (key: string) => string): string => {
  const parsed = parseRelative(relative);
  if (!parsed) return relative;
  const { n, unit } = parsed;
  const unitKey = n === 1
    ? `analytics.editForm.params.groupPeriodBy.${unit.slice(0, -1)}.item`
    : `analytics.editForm.params.period.relative.unit.${unit}`;
  return `${getTranslateFn('analytics.editForm.params.period.relative.label.last')} ${n} ${getTranslateFn(unitKey)?.toLowerCase()}`;
};

const detectInitialTab = (value: { dateFrom: string; dateTo?: string }): ActiveTab => {
  if (value?.dateFrom && value.dateFrom.startsWith('-')) {
    return 'relative';
  }
  return 'absolute';
};

class InputDateRange extends Component<Props, State> {
  private inputDate;
  private panelRef;
  static defaultProps = {
    withTime: true,
    withCloseButton: true,
    tooltip: 'common.enterDate.oneDate.incorrectFormat.text',
  };

  constructor(props) {
    super(props);

    this.state = {
      isOpened: false,
      isTopPosition: false,
      height: 'auto',
      left: 'unset',
      top: 'unset',
      maxWidth: '',
      activeTab: props.enableRelativeMode ? detectInitialTab(props.value) : 'absolute',
    };

    this.panelRef = React.createRef();
    this.attachEvents();
  }

  componentDidMount() {
    Tooltip.rebuild();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    if (this.state.isOpened && !prevState.isOpened) {
      this.setState({
        isTopPosition: isTopPositionByBlock(
          this.inputDate,
          this.props.containerSelector,
          '.ui-input-date__picker',
          true
        ),
      });
    }
  }

  componentWillUnmount(): void {
    this.detachEvents();
  }

  get relativeString(): string | undefined {
    const { value } = this.props;
    if (value?.dateFrom && value.dateFrom.startsWith('-')) {
      return value.dateFrom;
    }
    return undefined;
  }

  render() {
    const {
      id,
      onChange,
      value,
      label,
      minDate,
      maxDate,
      withTime,
      isRequired,
      customClass,
      getTranslate,
      applyFormat,
      modern,
      error,
      isDisabled,
      tooltip,
      enableRelativeMode,
      groupByDate,
    } = this.props;

    const { isOpened, isTopPosition, activeTab } = this.state;
    const isRelativeActive = enableRelativeMode && activeTab === 'relative';

    return (
      <div
        id={id}
        ref={(el) => {
          this.inputDate = el;
        }}
        className={classNames(
          'ui-input-date ui-input-date_range',
          customClass,
          {
            'ui-input-date_modern': !!modern,
            'ui-input-date_error': !!error,
            'ui-input-date_disabled': isDisabled,
            'ui-input-date_top': isTopPosition,
          }
        )}>
        <div
          className={classNames('ui-input-date__wrapper', {
            'ui-input-date__wrapper_modern': modern,
          })}>
          {(label || isRequired) && (
            <div className='ui-input-date__top'>
              <div>
                <span className='ui-input-date__label'>
                  {getTranslate(label)}
                  {isRequired && (
                    <span
                      data-tip={getTranslate('common.requiredField.tooltip')}
                      className='ui-input__required'>
                      *
                    </span>
                  )}
                </span>
                {tooltip && !modern && (
                  <InfoIcon
                    tooltip={getTranslate(tooltip)}
                    customClass='ui-input-date__info'
                  />
                )}
              </div>
            </div>
          )}

          {enableRelativeMode && this.relativeString ? (
            <div
              className={classNames('ui-manual-input-calendar ui-manual-input-calendar_range ui-input-date__range-field', {
                'ui-manual-input-calendar_disabled': isDisabled,
              })}>
              <div className='ui-manual-input-calendar__row'>
                <div
                  className='ui-manual-input-calendar__field'
                  onClick={isDisabled ? undefined : this.handleRelativeTriggerClick}>
                  <span className='ui-manual-input-calendar__placeholder ui-input-date__relative-trigger-label'>
                    {formatRelativeLabel(this.relativeString, getTranslate)}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <ManualInputCalendar
              id={`${id}`}
              isRange={true}
              onChange={(date) => {
                if (isRelativeActive) return;
                onChange(date);
              }}
              onClick={this.handleManualInputClick}
              value={value || { dateFrom: '', dateTo: '' }}
              onOpenCalendar={() => this.toggle(!isOpened)}
              withTime={withTime}
              isDisabled={isDisabled}
              minDate={minDate}
              maxDate={maxDate}
              isOpenedCalendar={isOpened}
              modern={modern}
              placeholder={this.props.placeholder}
              customClass='ui-input-date__range-field'
              isInnerBtn={false}
              disableButton={this.props.disableButton}
            />
          )}
          <Panel
            id='date-picker-range-panel'
            isOpened={isOpened}
            title={getTranslate(this.props.panelTitle || 'filter.date')}
            customClass='ui-panel_input-date'
            excludeFromCloseTrigger={['.ui-input-date__range-field']}
            onClose={() => this.toggle(false)}
            containerStyle={{
              height: this.state.height,
              left: this.state.left,
              top: this.state.top,
              maxWidth: this.state.maxWidth,
              ...this.props.panelPosition,
            }}
            panelRef={this.panelRef}
            modern>

            {enableRelativeMode && (
              <div className='ui-input-date__tabs'>
                <button
                  type='button'
                  className={classNames('ui-input-date__tab', {
                    'ui-input-date__tab_active': activeTab === 'absolute',
                  })}
                  onClick={() => this.handleTabChange('absolute')}>
                  {getTranslate('analytics.editForm.params.period.tab.absolute')}
                </button>
                <button
                  type='button'
                  className={classNames('ui-input-date__tab', {
                    'ui-input-date__tab_active': activeTab === 'relative',
                  })}
                  onClick={() => this.handleTabChange('relative')}>
                  {getTranslate('analytics.editForm.params.period.tab.relative')}
                </button>
              </div>
            )}


            {isRelativeActive  ? (
              <div className='ui-input-date__relative-picker'>
                <RelativeDatePicker
                  value={this.relativeString}
                  maxDays={getMaxDays(groupByDate)}
                  disabled={isDisabled}
                  onChange={(period: RelativePeriod) => {
                    onChange({ dateFrom: period.relative, dateTo: '' })
                    this.toggle(false);
                  }}
                />
              </div>
            ) : (
              <DatePickerRange
                range={{
                  from: value?.dateFrom || '',
                  to: value?.dateTo || '',
                }}
                showMonths={this.props.monthsAmount || 2}
                maxDate={this.props.maxDate}
                minDate={
                  minDate
                    ? DateHelpers.getFormat(
                        DateHelpers.createDate(minDate, applyFormat),
                        applyFormat
                      )
                    : undefined
                }
                onApplyRange={({ from, to }) => {
                  onChange({ dateFrom: from, dateTo: to });
                  this.toggle(false);
                }}
                applyFormat={applyFormat}
                withTime={withTime}
                hideManualDate={true}
                inputLabel='reports.new.dateAndTime.fromTo.label'
                isOpenedMenu={this.props.isMenuExpanded}
              />
            )}
          </Panel>
        </div>
        {error && (
          <div className='ui-input-date__error'>{getTranslate(error)}</div>
        )}
      </div>
    );
  }

  handleRelativeTriggerClick = () => {
    this.setState({ activeTab: 'relative' }, () => {
      this.toggle(true);
    });
  };

  handleTabChange = (tab: ActiveTab) => {
    this.setState({ activeTab: tab }, () => {
      if (tab === 'absolute') {
        setTimeout(this.getContentStyle, 50);
      } else {
        this.setState({height: 'auto'})
      }
    });
  };

  attachEvents = () => {
    document.addEventListener('mousedown', this.close);
    window.addEventListener('resize', this.getContentStyle);
  };

  detachEvents = () => {
    document.removeEventListener('mousedown', this.close);
    window.removeEventListener('resize', this.getContentStyle);
  };

  close = ({ target }) => {
    if (!target.closest(`.ui-input-date`)) {
      this.toggle(false);
    }
  };

  toggle = (isOpened) => {
    const { isDisabled, onDatePickerToggle } = this.props;
    if (!isDisabled) {
      this.setState({ isOpened: isOpened || false }, () => {
        onDatePickerToggle && onDatePickerToggle(isOpened);
        if (isOpened) {
          setTimeout(this.getContentStyle, 50);
        } else {
          this.setState({ height: 'auto' });
        }
      });
    }
  };

  onInputDate = ({ dateFrom }) => {
    this.props.onChange && this.props.onChange(dateFrom);
  };

  handleManualInputClick = (e) => {
    e.stopPropagation();
    if (this.state.isOpened || !this.props.disableButton) return;
    this.toggle(!this.state.isOpened);
  };

  getContentStyle = () => {
    if (window.innerWidth <= breakpoints.commonTablet) return;

    if (!this.panelRef.current) return;
    let heightToSet = 0;
    const inputPosition = this.inputDate.getBoundingClientRect();
    const contentHeight =
      this.panelRef.current.querySelector('div').clientHeight;
    const appLayout = document.querySelector('.layout-app__inner');

    if (appLayout && contentHeight) {
      const layoutHeight = appLayout.getBoundingClientRect().height;
      heightToSet =
        contentHeight < layoutHeight - inputPosition.bottom.toFixed()
          ? contentHeight
          : layoutHeight - inputPosition.bottom.toFixed();
    }

    this.setState({
      maxWidth: `${
        window.innerWidth - inputPosition.left.toFixed() - APP_PADDING
      }px`,
      height: `${heightToSet}px`,
      left: `${inputPosition.left.toFixed()}px`,
      top: `${(inputPosition.bottom + 5).toFixed()}px`,
    });

    this.panelRef.current.addEventListener('wheel', (e) => {
      e.stopPropagation();
    });

    this.panelRef.current
      .closest('.ui-scrollbar')
      .addEventListener('wheel', this.toggleOff);
  };

  toggleOff = () => {
    this.toggle(false);
  };
}

const mapStateToProps = (state) => ({
  isMenuExpanded: state.layoutConfiguration.isMenuExpanded,
});

export default connect(mapStateToProps)(addTranslation(InputDateRange));
